import { Component, DestroyRef, inject, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';

import { NumeronCollectionModel } from '../models/numeron.model';
import { NumeronsService } from '../services/numerons.service';
import { Ga4AnalyticsService } from '../../../core/analytics/ga4-analytics.service';
import { InfiniteScrollSentinelDirective } from '../../../shared/directives/infinite-scroll-sentinel.directive';
import { OrderedMasonryDirective } from '../../../shared/directives/ordered-masonry.directive';
import { OrderedMasonryItemDirective } from '../../../shared/directives/ordered-masonry-item.directive';
import { CollectionFeedCardComponent } from '../../../shared/ui/collection-feed-card/collection-feed-card.component';
import { NumeronCardComponent } from '../../../shared/ui/numeron-card/numeron-card.component';

@Component({
  selector: 'app-search-page',
  imports: [
    InfiniteScrollSentinelDirective,
    OrderedMasonryDirective,
    OrderedMasonryItemDirective,
    CollectionFeedCardComponent,
    NumeronCardComponent,
  ],
  templateUrl: './search-page.component.html',
})
export class SearchPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly numeronsService = inject(NumeronsService);
  private readonly ga4AnalyticsService = inject(Ga4AnalyticsService);
  private readonly title = inject(Title);
  private readonly destroyRef = inject(DestroyRef);

  private readonly pageSize = 10;
  private offset = 0;
  private currentQuery = '';

  protected readonly queryInput = signal('');
  protected readonly query = signal('');
  protected readonly collections = signal<NumeronCollectionModel[]>([]);
  protected readonly isLoading = signal(false);
  protected readonly isLoadingMore = signal(false);
  protected readonly hasError = signal(false);
  protected readonly hasMore = signal(false);

  public constructor() {
    this.route.queryParamMap
      .pipe(
        map((params) => (params.get('q') ?? '').trim()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((query) => {
        this.queryInput.set(query);
        this.query.set(query);
        this.currentQuery = query;
        this.title.setTitle(query ? `Search - ${query}` : 'Search');
        this.ga4AnalyticsService.trackPageView();
        if (query) {
          this.ga4AnalyticsService.trackEvent('view_search_results', {
            search_term: query,
          });
        }
        this.resetState();

        if (query) {
          this.hasMore.set(true);
          this.fetchSearch(true);
        }
      });
  }

  protected searchTitle(): string {
    const query = this.query();
    return query ? `Search Results: ${query}` : 'Search';
  }

  protected trackByCollectionId(_: number, collection: NumeronCollectionModel): string {
    return collection._id;
  }

  protected updateQueryInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.queryInput.set(target?.value ?? '');
  }

  protected submitSearch(event: Event): void {
    event.preventDefault();
    const query = this.queryInput().trim();

    if (query) {
      this.ga4AnalyticsService.trackEvent('search', {
        search_term: query,
      });
    }

    this.router.navigate(['/numerons/search'], {
      queryParams: query ? { q: query } : {},
    });
  }

  protected loadMore(): void {
    this.fetchSearch(false);
  }

  private resetState(): void {
    this.offset = 0;
    this.collections.set([]);
    this.isLoading.set(false);
    this.isLoadingMore.set(false);
    this.hasError.set(false);
    this.hasMore.set(false);
  }

  private fetchSearch(isInitialLoad: boolean): void {
    if (!this.currentQuery || !this.hasMore() || this.isLoading() || this.isLoadingMore()) {
      return;
    }

    this.hasError.set(false);

    if (isInitialLoad) {
      this.isLoading.set(true);
    } else {
      this.isLoadingMore.set(true);
    }

    this.numeronsService
      .getSearchNumeronCollections(this.currentQuery, this.offset, this.pageSize)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (incomingCollections) => {
          this.collections.update((existingCollections) =>
            isInitialLoad ? incomingCollections : [...existingCollections, ...incomingCollections],
          );
          this.offset += incomingCollections.length;
          this.hasMore.set(incomingCollections.length === this.pageSize);
          this.isLoading.set(false);
          this.isLoadingMore.set(false);
        },
        error: () => {
          this.hasError.set(true);
          this.isLoading.set(false);
          this.isLoadingMore.set(false);
        },
      });
  }
}
